import {
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
  type SnapshotOptions,
  Timestamp,
} from "firebase/firestore";
import type {
  OnboardingFlowConfig,
  Session,
  SessionMessage,
  User,
  OnboardingUser,
  Character,
  Chat,
  Message,
  LastMessageEntry,
} from "../types";

export const userConverter: FirestoreDataConverter<User> = {
  toFirestore: (u: User) => {
    const { id: _id, ...rest } = u;
    void _id; // <- consume it so ESLint is happy
    return rest;
  },
  fromFirestore: (snap: QueryDocumentSnapshot, options: SnapshotOptions) => {
    return { id: snap.id, ...(snap.data(options) as Omit<User, "id">) };
  },
};

export const onboardingUserConverter: FirestoreDataConverter<OnboardingUser> = {
  toFirestore: (u: OnboardingUser) => {
    const { id: _id, ...rest } = u;
    void _id; // <- consume it so ESLint is happy
    return rest;
  },
  fromFirestore: (snap: QueryDocumentSnapshot, options: SnapshotOptions) => {
    return {
      id: snap.id,
      ...(snap.data(options) as Omit<OnboardingUser, "id">),
    };
  },
};

export const sessionConverter: FirestoreDataConverter<Session> = {
  toFirestore: (s: Session) => {
    const { id: _id, createdAt, updatedAt, ...rest } = s;
    void _id; // <- consume it so ESLint is happy
    return {
      ...rest,
      createdAt: Timestamp.fromDate(createdAt),
      updatedAt: Timestamp.fromDate(updatedAt),
    };
  },
  fromFirestore: (snap: QueryDocumentSnapshot, options: SnapshotOptions) => {
    const data = snap.data(options) as Omit<Session, "id" | "createdAt" | "updatedAt"> & {
      createdAt: Timestamp;
      updatedAt: Timestamp;
    };
    return {
      id: snap.id,
      ...data,
      createdAt: data.createdAt.toDate(),
      updatedAt: data.updatedAt.toDate(),
    };
  },
};

export const sessionMessageConverter: FirestoreDataConverter<SessionMessage> = {
  toFirestore: (sm: SessionMessage) => {
    const { id: _id, createdAt, updatedAt, ...rest } = sm;
    void _id; // <- consume it so ESLint is happy
    return {
      ...rest,
      createdAt: Timestamp.fromDate(createdAt),
      updatedAt: Timestamp.fromDate(updatedAt),
    };
  },
  fromFirestore: (snap: QueryDocumentSnapshot, options: SnapshotOptions) => {
    const data = snap.data(options) as Omit<SessionMessage, "id" | "createdAt" | "updatedAt"> & {
      createdAt: Timestamp;
      updatedAt: Timestamp;
    };
    return {
      id: snap.id,
      ...data,
      createdAt: data.createdAt.toDate(),
      updatedAt: data.updatedAt.toDate(),
    };
  },
};

export const onboardingFlowConverter: FirestoreDataConverter<OnboardingFlowConfig> =
  {
    toFirestore: (flow: OnboardingFlowConfig) => {
      return flow;
    },
    fromFirestore: (snap: QueryDocumentSnapshot, options: SnapshotOptions) => {
      const data = snap.data(options) as OnboardingFlowConfig;

      return {
        ...data,
        questions: data.questions.map((question) => {
          // Derive selectionMode for choice questions based on maxSelections
          if (question.kind === "choice") {
            const isMulti =
              question.maxSelections === null || question.maxSelections > 1;

            return {
              ...question,
              selectionMode: isMulti ? "multi" : "single",
            };
          }

          return question;
        }),
      };
    },
  };

export const characterConverter: FirestoreDataConverter<Character> = {
  toFirestore: (c: Character) => {
    const { id: _id, createdAt, updatedAt, ...rest } = c;
    void _id;
    return {
      ...rest,
      createdAt: Timestamp.fromDate(createdAt),
      updatedAt: Timestamp.fromDate(updatedAt),
    };
  },
  fromFirestore: (snap: QueryDocumentSnapshot, options: SnapshotOptions) => {
    const data = snap.data(options) as Omit<Character, "id" | "createdAt" | "updatedAt"> & {
      createdAt: Timestamp;
      updatedAt: Timestamp;
    };
    return {
      id: snap.id,
      ...data,
      createdAt: data.createdAt.toDate(),
      updatedAt: data.updatedAt.toDate(),
    };
  },
};

type LastMessageEntryFirestore = Omit<LastMessageEntry, "createdAt"> & {
  createdAt: Timestamp;
};

export const chatConverter: FirestoreDataConverter<Chat> = {
  toFirestore: (c: Chat) => {
    const {
      id: _id,
      lastMessages,
      createdAt,
      character: _character,
      shownMediaMeta,
      lastReadAt,
      lastCharacterMessageAt,
      updatedAt,
      ...rest
    } = c;
    void _id;
    void _character;

    // Convert lastMessages entries to Firestore format
    const lastMessagesFirestore: Record<string, LastMessageEntryFirestore> = {};
    for (const [participantId, entry] of Object.entries(lastMessages)) {
      lastMessagesFirestore[participantId] = {
        ...entry,
        createdAt: Timestamp.fromDate(entry.createdAt),
      };
    }

    return {
      ...rest,
      lastMessages: lastMessagesFirestore,
      shownMediaMeta: shownMediaMeta?.map((entry) => ({
        ...entry,
        lastSeenAt: Timestamp.fromDate(entry.lastSeenAt),
      })),
      lastReadAt: lastReadAt ? Timestamp.fromDate(lastReadAt) : undefined,
      lastCharacterMessageAt: lastCharacterMessageAt
        ? Timestamp.fromDate(lastCharacterMessageAt)
        : undefined,
      createdAt: Timestamp.fromDate(createdAt),
      updatedAt: updatedAt ? Timestamp.fromDate(updatedAt) : undefined,
    };
  },
  fromFirestore: (snap: QueryDocumentSnapshot, options: SnapshotOptions) => {
    const data = snap.data(options) as Omit<
      Chat,
      "id" | "lastMessages" | "createdAt" | "shownMediaMeta"
    > & {
      lastMessages: Record<string, LastMessageEntryFirestore>;
      createdAt: Timestamp;
      characterId?: string;
      character?: Chat["character"];
      shownMediaMeta?: Array<{
        id: string;
        lastSeenAt: Timestamp;
      }>;
      lastReadAt?: Timestamp;
      lastCharacterMessageAt?: Timestamp;
      updatedAt?: Timestamp;
      scriptState?: Omit<
        NonNullable<Chat["scriptState"]>,
        "startedAt" | "completedAt"
      > & {
        startedAt?: Timestamp;
        completedAt?: Timestamp;
      };
    };

    // Convert lastMessages entries from Firestore format
    const lastMessages: Record<string, LastMessageEntry> = {};
    if (data.lastMessages) {
      for (const [participantId, entry] of Object.entries(data.lastMessages)) {
        lastMessages[participantId] = {
          ...entry,
          createdAt: entry.createdAt.toDate(),
        };
      }
    }

    const characterId = data.characterId ?? data.character?.id ?? "";
    const shownMediaMeta = data.shownMediaMeta?.map((entry) => ({
      ...entry,
      lastSeenAt: entry.lastSeenAt.toDate(),
    }));
    const lastReadAt = data.lastReadAt?.toDate();
    const lastCharacterMessageAt = data.lastCharacterMessageAt?.toDate();
    const updatedAt = data.updatedAt?.toDate();
    // scriptState.startedAt/completedAt are Firestore Timestamps on the wire
    // (see ChatDocument.scriptState in apps/functions/src/types/types.ts) —
    // convert to Date like every other date field on this doc, otherwise
    // consumers (e.g. ChatThread.tsx) get a raw Timestamp where a Date is
    // expected.
    const scriptState = data.scriptState
      ? {
          ...data.scriptState,
          startedAt: data.scriptState.startedAt?.toDate(),
          completedAt: data.scriptState.completedAt?.toDate(),
        }
      : undefined;

    return {
      id: snap.id,
      ...data,
      characterId,
      lastMessages,
      shownMediaMeta,
      lastReadAt,
      lastCharacterMessageAt,
      createdAt: data.createdAt.toDate(),
      updatedAt,
      scriptState,
    };
  },
};

export const messageConverter: FirestoreDataConverter<Message> = {
  toFirestore: (m: Message) => {
    const { id: _id, createdAt, ...rest } = m;
    void _id;
    return {
      ...rest,
      createdAt: Timestamp.fromDate(createdAt),
    };
  },
  fromFirestore: (snap: QueryDocumentSnapshot, options: SnapshotOptions) => {
    const data = snap.data(options) as Omit<Message, "id" | "createdAt"> & {
      createdAt: Timestamp;
    };
    return {
      id: snap.id,
      ...data,
      createdAt: data.createdAt.toDate(),
    };
  },
};
